import { defineStore } from 'pinia';
import { ApiService, TranslationKey, TranslationService } from "webshop-modules";
import { PromotionStoreState } from "@/domain/ticket-selection/entities/PromotionStoreState.interface";
import { AxiosResponse } from "axios";
import { ErrorResponse } from "webshop-api";
import { TranslationPlugin } from "@/plugins/translation.plugin";

export const usePromotionStore = defineStore('promotion', {
    state: (): PromotionStoreState => ({
        active: null,
        availablePromotions: [],
        error: null,
        selection: null,
        showHeader: true,
        spinner: false,
        code: null,
    }),

    getters: {
        activePromotion: (state) => state.active,
        promotionCode: (state) => state.code && state.active ? state.code : null,
        canDisplayHeader: (state) => state.showHeader && state.active !== null,
    },

    actions: {
        activate(promotion) {
            this.active = promotion;
        },

        select(promotion) {
            this.selection = promotion;
        },

        setAvailable(promotions) {
            this.availablePromotions = promotions;
        },

        setCode(code) {
            this.code = code;
        },

        setError(content) {
            this.error = content;
        },

        setHeaderDisplay(state) {
            this.showHeader = state;
        },

        displaySpinner() {
            this.spinner = true;
        },

        hideSpinner() {
            this.spinner = false;
        },

        initializePromotions(data) {
            this.setAvailable(data);
        },

        async removePromotion() {
            this.setError(null);
            this.select(null);
            this.setCode(null);
            this.activate(null);
        },

        async selectPromotion(data) {
            this.setError(null);
            this.select(data);
        },

        changeHeaderDisplay(state) {
            this.setHeaderDisplay(state);
        },

        async activatePromotion(translationService: TranslationService) {
            if (this.selection === null) {
                this.setError(await translationService.getTranslationFor(TranslationKey.Promotion_error_not_selected));

                return false;
            }

            this.setError(null);
            this.activate(this.selection);
            this.hideSpinner();
        },

        async validatePromotion(
            param: { code: string, eventId: number },
            translationService: TranslationService,
            api: ApiService,
        ) {
            if (this.selection === null) {
                this.setError(await translationService.getTranslationFor(TranslationKey.Promotion_error_not_selected));
                return false;
            }

            if (!param.code || param.code.length === 0) {
                const errorTranslation = await translationService.getTranslationFor(TranslationKey.Promotion_error_not_entered);
                const translationData = {
                    key: TranslationKey.Promotion_error_not_entered,
                    keyValues: JSON.stringify({ promotionName: this.selection.name }),
                    identifierStart: '{',
                    identifierEnd: '}'
                };

                this.setError(TranslationPlugin.decorateWithParams(errorTranslation, translationData));
                this.setCode(null);
                return false;
            }

            this.displaySpinner();

            try {
                const response = await api.validatePromotionCode({
                    poolId: api.configuration.clientId,
                    promotionId: this.selection.id ?? 0,
                    eventId: param.eventId,
                    code: param.code
                });

                if (!response.successful) {
                    this.setError(await translationService.getTranslationFor(response.error as TranslationKey));
                    this.hideSpinner();
                    return false;
                }

                this.setCode(param.code);
                await this.activatePromotion(translationService);
            } catch (e) {
                const error = (<AxiosResponse> e).data as ErrorResponse;

                if (error !== undefined) {
                    this.setError(error.message);
                    this.hideSpinner();
                    return false;
                }

                this.setError('An error occurred');
                this.hideSpinner();
                return false;
            }
        },
    },
});

